import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { EligibleVoterResponseDto } from '@thro-clients/polyas-connector-api';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import { translations } from 'locales/i18n';

interface VoterTableProps {
  voters: EligibleVoterResponseDto[];
}

export function VoterTable({ voters }: VoterTableProps) {
  const { t } = useTranslation();

  if (voters.length === 0) {
    return (
      <Paper variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
        <EmailIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {t(translations.pages.polyas.voter.table.noVoters())}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mx: 'auto' }}>
          {t(translations.pages.polyas.voter.table.addVotersHint())}
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>{t(translations.pages.polyas.voter.import.firstName())}</TableCell>
            <TableCell>{t(translations.pages.polyas.voter.import.lastName())}</TableCell>
            <TableCell>{t(translations.pages.polyas.voter.import.email())}</TableCell>
            <TableCell>{t(translations.pages.polyas.voter.table.votingGroups())}</TableCell>
            <TableCell>{t(translations.pages.polyas.voter.management.status())}</TableCell>
            <TableCell width={48}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {voters.map(voter => (
            <TableRow
              key={voter.eligibleVoterUser.userUuid}
              sx={{
                bgcolor: 'transparent'
              }}
            >
              <TableCell>{voter.eligibleVoterUser.firstName}</TableCell>
              <TableCell>{voter.eligibleVoterUser.lastName}</TableCell>
              <TableCell>{voter.eligibleVoterUser.email}</TableCell>
              <TableCell>
                {voter.votingGroups && voter.votingGroups.length > 0
                  ? voter.votingGroups.join(', ')
                  : '-'}
              </TableCell>
              <TableCell>
                {voter.hasElectionMarkedAsVoted ? (
                  <Tooltip
                    title={`${t(translations.pages.polyas.voter.table.markedAt())} ${format(
                      new Date(voter?.hasElectionMarkedAsVoted),
                      "dd.MM.yyyy 'um' HH:mm",
                      { locale: de }
                    )}`}
                  >
                    <Chip
                      icon={<CheckIcon />}
                      label={t(translations.pages.polyas.voter.table.voted())}
                      size="small"
                      color="success"
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    icon={<CloseIcon />}
                    label={t(translations.pages.polyas.voter.table.pending())}
                    size="small"
                    variant="outlined"
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
